import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Modal from "../../../../../components/Modal/Modal";
import ChevronLeftIcon from "../../../../../assets/svg/chevron-left-icon";
import ChevronRightIcon from "../../../../../assets/svg/chevron-right-icon";
import { getFileTypeIcon } from "../../../../../utils/functions/file-icon-extractor";
import DownloadIcon from "../../../../../assets/svg/download-icon";
import Button from "../../../../../components/Button/Button";
import {
  AttachmentPreviewItem,
  AttachmentPreviewModalProps,
} from "../../../utils/types";
import { isImage, isPdf, isText, isSpreadsheet } from "../../../utils/functions";
import SpreadSheetViewer, {
  type SpreadsheetRow,
} from "../../../../../components/Common/SpreadSheetViewer";
import * as Dialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { getDateTimeByFormat } from "../../../../../redux/slices/dateTimeFormatSlice";
import CircularLoader from "../../../../../components/Loader/CircularLoader";

// Text File Preview Component
const TextFilePreview: React.FC<{
  fileUrl: string;
  fileName: string;
}> = ({ fileUrl, fileName }) => {
  const [textContent, setTextContent] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const fetchTextContent = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`);
        }
        const text = await response.text();
        setTextContent(text);
      } catch (err) {
        console.error("Error fetching text file:", err);
        setError(err instanceof Error ? err.message : "Failed to load file");
      } finally {
        setLoading(false);
      }
    };

    fetchTextContent();
  }, [fileUrl]);

  if (loading) {
    return (
      <div className="w-full h-[55vh] flex items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-verba-blue dark:border-verba-blue-dk mx-auto mb-2"></div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Loading {fileName}...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-[55vh] flex items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="text-center p-4">
          <p className="text-red-500 dark:text-red-400 mb-2">
            Failed to load text file
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">{error}</p>
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-2 text-verba-blue dark:text-verba-blue-dk hover:underline"
          >
            Open in new tab
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-[55vh] bg-white dark:bg-gray-900 rounded-lg border border-border-gray dark:border-border-gray-dk overflow-hidden">
      <div className="h-full overflow-auto p-4">
        <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono leading-relaxed">
          {textContent}
        </pre>
      </div>
    </div>
  );
};

// Excel Spreadsheet Preview Component — parses the workbook client-side (exceljs
// is lazily imported so it only loads when a spreadsheet is previewed) and renders
// the first sheet via SpreadSheetViewer. This avoids the Google Docs Viewer, which
// can't reach the attachment URLs and rendered blank for Excel files.
const SpreadsheetFilePreview: React.FC<{
  fileUrl: string;
  fileName: string;
}> = ({ fileUrl, fileName }) => {
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<SpreadsheetRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;

    // exceljs cells can be strings, numbers, dates, or rich objects
    // (formulas, hyperlinks, rich text) — flatten each to display text.
    const cellToString = (value: unknown): string => {
      if (value == null) return "";
      if (value instanceof Date) return value.toLocaleDateString();
      if (typeof value === "object") {
        const cell = value as {
          text?: unknown;
          result?: unknown;
          hyperlink?: unknown;
          error?: unknown;
          richText?: { text?: string }[];
        };
        if (Array.isArray(cell.richText))
          return cell.richText.map((part) => part?.text ?? "").join("");
        if (cell.text != null) return String(cell.text);
        if (cell.result != null) return String(cell.result);
        if (cell.hyperlink != null) return String(cell.hyperlink);
        if (cell.error != null) return String(cell.error);
        return "";
      }
      return String(value);
    };

    const loadSpreadsheet = async () => {
      try {
        setLoading(true);
        setError(false);
        const response = await fetch(fileUrl, { cache: "no-store" });
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`);
        }
        const buffer = await response.arrayBuffer();
        // Lazy import keeps exceljs (a large lib) out of the main bundle.
        const { Workbook } = await import("exceljs");
        const workbook = new Workbook();
        await workbook.xlsx.load(buffer);

        const worksheet = workbook.worksheets?.[0];
        if (!worksheet) throw new Error("No sheets found in workbook");

        const parsedHeaders: string[] = [];
        const parsedRows: SpreadsheetRow[] = [];

        worksheet.eachRow((row, rowNumber) => {
          // exceljs pads row.values with an empty slot at index 0.
          const values = Array.isArray(row.values) ? row.values.slice(1) : [];
          if (rowNumber === 1) {
            values.forEach((cell, index) => {
              parsedHeaders.push(cellToString(cell) || `Column ${index + 1}`);
            });
          } else {
            const record: SpreadsheetRow = {};
            parsedHeaders.forEach((header, index) => {
              record[header] = cellToString(values[index]);
            });
            parsedRows.push(record);
          }
        });

        if (cancelled) return;
        setHeaders(parsedHeaders);
        setRows(parsedRows);
      } catch (err) {
        if (cancelled) return;
        console.error("Error parsing spreadsheet:", err);
        setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadSpreadsheet();
    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  if (loading) {
    return (
      <div className="w-full h-[55vh] flex items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-verba-blue dark:border-verba-blue-dk mx-auto mb-2"></div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Loading {fileName}...
          </p>
        </div>
      </div>
    );
  }

  if (error || headers.length === 0) {
    return (
      <div className="w-full h-[55vh] flex items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="text-center p-4">
          <img
            src={getFileTypeIcon(fileName)}
            alt={fileName}
            className="w-12 h-12 mx-auto mb-3"
          />
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            {error
              ? "Preview isn't available for this file."
              : "This spreadsheet is empty."}
          </p>
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-verba-blue dark:text-verba-blue-dk hover:underline"
          >
            Open in new tab
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-[55vh] overflow-auto rounded-lg border border-border-gray dark:border-border-gray-dk bg-white">
      <SpreadSheetViewer headers={headers} data={rows} />
    </div>
  );
};

const MAX_THUMBNAILS_VISIBLE = 5;
const THUMBNAIL_WIDTH = 48;
const THUMBNAIL_GAP = 8;
const MAX_THUMBNAILS_WIDTH =
  MAX_THUMBNAILS_VISIBLE * THUMBNAIL_WIDTH +
  (MAX_THUMBNAILS_VISIBLE - 1) * THUMBNAIL_GAP;

const AttachmentPreviewModal: React.FC<AttachmentPreviewModalProps> = ({
  open,
  onOpenChange,
  attachments,
  initialIndex = 0,
  title = "",
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState<number>(initialIndex || 0);
  const containerRef = useRef<HTMLDivElement>(null);
  const thumbsScrollRef = useRef<HTMLDivElement>(null);

  const current = attachments[currentIndex];

  const prev = useCallback(() => {
    setCurrentIndex((i) => (i - 1 + attachments.length) % attachments.length);
  }, [attachments.length]);

  const next = useCallback(() => {
    setCurrentIndex((i) => (i + 1) % attachments.length);
  }, [attachments.length]);

  const handleDownload = useCallback(async () => {
    try {
      const file = attachments[currentIndex];
      if (!file?.s3_url) return;
      setIsLoading(true);
      const fetchResponse = await fetch(file.s3_url, {
        cache: "no-store",
      });
      const blob = await fetchResponse.blob();

      // Create blob URL and download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = file.file_name || "download";

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, [attachments, currentIndex]);

  // Helper function to check if file is Microsoft Office format
  const isMicrosoftOffice = (item: AttachmentPreviewItem): boolean => {
    const contentType = item.content_type || "";
    const fileName = item.file_name || "";

    const officeTypes = [
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
      "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "application/msword", // .doc
      "application/vnd.ms-powerpoint", // .ppt
      "application/vnd.ms-excel", // .xls
    ];

    const officeExtensions = [
      ".docx",
      ".pptx",
      ".xlsx",
      ".doc",
      ".ppt",
      ".xls",
    ];

    return (
      officeTypes.includes(contentType) ||
      officeExtensions.some((ext) => fileName.toLowerCase().endsWith(ext))
    );
  };

  // Helper function to check if file is a text-like file (for additional extensions)
  const isTextFile = (item: AttachmentPreviewItem): boolean => {
    const fileName = item.file_name || "";

    const textExtensions = [".log", ".md", ".json", ".xml", ".yaml", ".yml"];

    return textExtensions.some((ext) => fileName.toLowerCase().endsWith(ext));
  };

  const mainPreview = useMemo(() => {
    if (!current) return null;

    // Handle images with optimized display
    if (isImage(current)) {
      return (
        <img
          src={current.s3_url}
          alt={current.file_name}
          className="max-h-[55vh] max-w-full object-contain rounded-lg"
          loading="eager"
        />
      );
    }

    // Handle PDFs with specialized viewer
    if (isPdf(current)) {
      return (
        <object
          data={current.s3_url}
          type="application/pdf"
          className="w-full h-[55vh] rounded-lg"
        >
          <iframe
            src={current.s3_url}
            className="w-full h-[55vh] rounded-lg"
            title={current.file_name || "PDF Preview"}
          />
        </object>
      );
    }

    // Handle text files with custom text viewer
    if (isText(current) || isTextFile(current)) {
      return (
        <TextFilePreview
          fileUrl={current.s3_url}
          fileName={current.file_name || "Text File"}
        />
      );
    }

    // Handle Excel spreadsheets with a native client-side table viewer
    if (isSpreadsheet(current)) {
      return (
        <SpreadsheetFilePreview
          fileUrl={current.s3_url}
          fileName={current.file_name || "Spreadsheet"}
        />
      );
    }

    // Handle Microsoft Office files with Google Docs Viewer
    if (isMicrosoftOffice(current)) {
      const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
        current.s3_url
      )}&embedded=true`;
      return (
        <iframe
          src={viewerUrl}
          className="w-full h-[55vh] rounded-lg border border-border-gray dark:border-border-gray-dk"
          title={current.file_name || "Office Document Preview"}
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
        />
      );
    }

    // Handle all other file types with iframe
    return (
      <iframe
        src={current.s3_url}
        className="w-full h-[55vh] rounded-lg border border-border-gray dark:border-border-gray-dk"
        title={current.file_name || "File Preview"}
        sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
      />
    );
  }, [current]);

  const renderThumb = (item: AttachmentPreviewItem, index: number) => {
    const selected = index === currentIndex;
    if (isImage(item)) {
      return (
        <Button
          id={`attachment-thumb-${index}`}
          onClick={() => setCurrentIndex(index)}
          ariaLabel={`Open ${item.file_name}`}
          buttonContainerClass={`w-[48px] h-[48px] p-[1px] shrink-0 rounded overflow-hidden flex items-center justify-center border hover:opacity-90 cursor-pointer snap-center ${
            selected
              ? "border-verba-blue dark:border-verba-blue-dk"
              : "border-border-gray dark:border-border-gray-dk"
          } bg-gray-50 dark:bg-gray-800`}
          innerText={
            <img
              src={item.s3_url}
              alt={item.file_name}
              className="w-full h-full object-cover"
            />
          }
        />
      );
    }

    const icon = getFileTypeIcon(item.file_name || "document");
    return (
      <Button
        id={`attachment-thumb-${index}`}
        onClick={() => setCurrentIndex(index)}
        ariaLabel={`Open ${item.file_name}`}
        buttonContainerClass={`w-[48px] h-[48px] p-[1px] shrink-0 rounded overflow-hidden flex items-center justify-center border hover:opacity-90 cursor-pointer snap-center ${
          selected
            ? "border-verba-blue dark:border-verba-blue-dk"
            : "border-border-gray dark:border-border-gray-dk"
        } bg-gray-50 dark:bg-gray-800`}
        innerText={<img src={icon} alt={item.file_name} className="w-8 h-8" />}
      />
    );
  };

  useLayoutEffect(() => {
    const container = thumbsScrollRef.current;
    if (!container) return;
    const activeEl = container.querySelector(
      `#attachment-thumb-${currentIndex}`
    ) as HTMLElement | null;
    if (!activeEl) return;
    activeEl.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [currentIndex]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, currentIndex, attachments.length, next, prev]);

  useEffect(() => {
    setCurrentIndex(initialIndex || 0);
  }, [initialIndex, attachments.length, open]);

  const formattedTimestamp = getDateTimeByFormat(
    new Date(current?.timestamp || "")
  );

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      showHeader={true}
      preventOutsideClick={false}
      description={`Preview of ${current?.file_name || "attachment"}`}
      contentClassName="z-[100] w-[80vw] max-w-[1200px] h-[80vh] !p-0 vf-glass-scrollbar bg-pure-white dark:bg-pure-white-dk"
      bodyClassName="!p-[18px] bg-pure-white dark:bg-pure-white-dk"
      showCloseButton={true}
      className=""
      customHeader={
        <>
          <Dialog.DialogTitle asChild>
            <div className="flex flex-col w-full">
              <div className="w-full flex items-center gap-2 min-w-0">
                <img
                  src={getFileTypeIcon(current?.file_name || "image")}
                  className="w-[15px] h-[18px] object-contain"
                />
                <p className="truncate max-w-[60vw] text-md font-semibold text-heading-text dark:text-heading-text-dk">
                  {current?.file_name || title}
                </p>
              </div>
              {formattedTimestamp?.date && formattedTimestamp?.time && (
                <p className="text-xs text-dark-gray dark:text-dark-gray-dk">
                  {formattedTimestamp?.date} | {formattedTimestamp?.time}
                </p>
              )}
            </div>
          </Dialog.DialogTitle>
          <Dialog.Description asChild>
            <VisuallyHidden>
              Preview of {current?.file_name || "attachment"}
            </VisuallyHidden>
          </Dialog.Description>
          <Button
            Icon={
              isLoading
                ? () => (
                    <CircularLoader
                      size={14}
                      className="text-verba-blue dark:text-verba-blue-dk w-[14px] h-[14px"
                    />
                  )
                : DownloadIcon
            }
            iconLeft
            iconClass="text-verba-blue dark:text-verba-blue-dk w-[14px] h-[14px]"
            innerText="Download"
            innerTextClass="text-md text-verba-blue dark:text-verba-blue-dk font-medium"
            buttonContainerClass="w-fit mr-4"
            onClick={handleDownload}
          />
        </>
      }
    >
      <div
        ref={containerRef}
        className="flex flex-col w-full h-full gap-6  justify-between "
      >
        <div className="relative flex-1 bg-pure-white dark:bg-pure-white-dk flex items-center justify-center">
          <div className="w-full h-[55vh] max-h-[55vh] min-h-[55vh] flex items-center justify-center ">
            {mainPreview}
          </div>
        </div>
        {attachments.length > 0 && (
          <div className="relative w-full flex items-center justify-center gap-6">
            {attachments.length > 1 && (
              <Button
                Icon={ChevronLeftIcon}
                iconClass="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk"
                onClick={prev}
                ariaLabel="Previous attachment"
                buttonContainerClass="rounded-full border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk w-[26px] h-[26px] px-2 py-[6px] flex items-center justify-center"
              />
            )}
            <div
              ref={thumbsScrollRef}
              className="flex items-center gap-2 overflow-x-auto snap-x snap-mandatory no-scrollbar"
              style={{ maxWidth: MAX_THUMBNAILS_WIDTH }}
            >
              {attachments.map((att: AttachmentPreviewItem, idx: number) => (
                <div key={`attachment-${idx}`}>{renderThumb(att, idx)}</div>
              ))}
            </div>

            {attachments.length > 1 && (
              <Button
                Icon={ChevronRightIcon}
                iconClass="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk"
                onClick={next}
                ariaLabel="Next attachment"
                buttonContainerClass="rounded-full border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk w-[26px] h-[26px] px-2 py-[6px] flex items-center justify-center"
              />
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AttachmentPreviewModal;
